import { useTranslation } from "react-i18next";

import { PageTable } from "@components/organisms/pageTable";
import { AppointmentCard } from "@components/organisms/appointmentCard";
import type { IAppointmentDataTableEndpoint } from "@api/appointmentData";
import { getFullName, useConvertDate } from "@utils";
import type { ITable } from "@/interfaces";
import type { ID } from "@constants";
import type { IMspVariants } from "@components/organisms/appointmentCard/components/appointmentCardBottom";

export function AppointmentMspList(
  props: ITable<IAppointmentDataTableEndpoint> & {
    onDownloadItem?: (id: ID) => void;
    onPrintItem?: (id: ID) => void;
    cardVariant?: IMspVariants;
  },
) {
  const { t } = useTranslation("common");
  const {
    data,
    onPrintItem,
    onDownloadItem,
    isLoading,
    isError,
    isFetching,
    tableParamProps,
    total,
    cardVariant,
  } = props;

  const { humanizeFullDate } = useConvertDate();

  return (
    <PageTable
      type={"row"}
      title={t("appointmentTableTitle")}
      placeholder={"visitList"}
      total={total}
      isFetching={isFetching}
      isError={isError}
      isLoading={isLoading}
      {...tableParamProps}
    >
      {data?.map((item) => (
        <AppointmentCard
          variant={
            cardVariant ||
            (tableParamProps?.tabProps?.selectedKey as IMspVariants) ||
            "msp_prescription"
          }
          id={item?.id}
          key={item?.id}
          status={item?.status}
          data={{
            doctorInfo: {
              id: item?.doctor?.user_id,
              name: item?.doctor?.first_name,
              lastName: item?.doctor?.last_name,
              avatarUrl: item?.doctor?.avatar?.url,
              details: {
                degree: item?.doctor?.degree?.value,
                speciality: item?.doctor?.speciality?.value,
              },
            },
            patientInfo: {
              id: item?.patient?.user_id,
              name: item?.patient?.first_name,
              lastName: item?.patient?.last_name,
              avatarUrl: item?.patient?.avatar?.url,
              details: {
                age: item?.patient?.age,
                gender: item?.patient?.gender?.value,
              },
            },
            appointmentInfo: {
              id: item?.appt_id,
              name: item?.facility?.value,
              details: { date: humanizeFullDate(item?.date) },
            },
          }}
          actions={{
            canDownload: true,
            canPrint: true,
            onDownload: onDownloadItem && (() => onDownloadItem?.(item?.id)),
            onPrint: onPrintItem && (() => onPrintItem?.(item?.id)),
          }}
          appointmentType={"all"}
          summaryNoteId={item?.summary_note_id}
          doctorName={getFullName({
            firstName: item?.doctor?.first_name,
            lastName: item?.doctor?.last_name,
          })}
        />
      ))}
    </PageTable>
  );
}
